import { ApiProductsResponse, useProductsInfiniteQuery } from '@/api/products';
import { CASUAL_LIMIT } from '@/pages/catalog';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import CatalogPreloader from '@/components/shared/CatalogPreloader';
import { AnimatePresence } from 'framer-motion';
import { useSearchCountState } from '@/atoms/catalog/search-count';
import { useSearchTitleState } from '@/atoms/catalog/search-title';
import Button from '@/components/shared/Button';
import { useSetOpenedPopupsState } from '@/atoms/opened-popups';
import { useSetSelectedServiceValueFormState } from '@/atoms/form/selected-service-value-form-state';
import { FEEDBACK_POPUP_NAME } from '../FeedbackPopup/FeedbackPopup';
import { unlockBodyScroll } from '@/utils/dom';
import { FILTERS_POPUP_NAME } from '@/components/layout/CatalogFilters/FiltersPopup';
import { SORT_POPUP_NAME } from '@/components/layout/CatalogFilters/SortPopup';
import equalArrays from 'lodash.isequal';
import Card from '@/components/shared/Card';
import { FilterFields } from '@/api/productFilters';
import { useSetFeedbackPopupDataNameState } from '../FeedbackPopup/use-feedback-popup-data-name-atom';
import NewPagination from '@/components/shared/NewPagination';
import ArrowMoreSVG from '@/svg/arrow-more.svg?url';
import classNames from 'classnames';
import { useActualPageStateValue, useSetActualPageState } from '@/atoms/actual-page';

type Props = {
    titleIndex?: number;
    title?: string;
    initialResponse: ApiProductsResponse;
    initialQueryState: Record<string, any>;
    filters?: Partial<FilterFields>;
};

const catalogEmptyText =
    'Ничего не&nbsp;найдено. Попробуйте изменить параметры фильтров, и&nbsp;мы&nbsp;поищем ещё раз. Или подайте заявку на&nbsp;поиск нужного товара.';

const CatalogListWithPagination = ({ titleIndex, title, initialResponse, initialQueryState, filters }: Props) => {
    const router = useRouter();
    const page = Math.max(1, Number(router.query.page) || initialQueryState.page || 1);

    const [category, setCategory] = useState(initialQueryState.category);
    const [available, setAvailable] = useState(initialQueryState.available || undefined);
    const [sort, setSort] = useState(initialQueryState.sort || undefined);
    const [order, setOrder] = useState(initialQueryState.order || undefined);
    const [search, setSearch] = useState(initialQueryState.search || undefined);

    const [, setSearchCount] = useSearchCountState();
    const [, setSearchTitle] = useSearchTitleState();
    const wrapper = useRef<HTMLDivElement>(null);
    const [materials, setMaterials] = useState<number[] | undefined>(
        initialQueryState['materials[]']?.map((item: number) => Number(item)),
    );
    const [type, setType] = useState<string[]>(initialQueryState['type[]']);
    const [color, setColor] = useState<string[]>(initialQueryState['color[]']);
    const [insert, setInsert] = useState<string[]>(initialQueryState['insert[]']);
    const [collection, setCollection] = useState<string[]>(initialQueryState['collection[]']);
    const [dialColors, setDialColors] = useState<string[]>(initialQueryState['dial_color[]']);
    const [options, setOptions] = useState<string[]>(initialQueryState['options[]']);
    const [shapes, setShapes] = useState<string[]>(initialQueryState['shapes[]']);
    const [bracelets, setBracelets] = useState<string[]>(initialQueryState['bracelets[]']);
    const [mechanisms, setMechanisms] = useState<string[]>(initialQueryState['mechanisms[]']);
    const [genders, setGenders] = useState<number[]>(initialQueryState['genders[]']);
    const [brands, setBrands] = useState<number[] | undefined>(
        initialQueryState['brands[]']?.map((item: number) => Number(item)),
    );
    const { openPopup } = useSetOpenedPopupsState();
    const setServiceValueFormState = useSetSelectedServiceValueFormState();
    const [productState, setProductState] = useState(initialQueryState['state[]'] || undefined);
    const [priceMin, setPriceMin] = useState<number | undefined>(initialQueryState.priceMin ?? undefined);
    const [priceMax, setPriceMax] = useState<number | undefined>(initialQueryState.priceMax ?? undefined);
    const [minPower, setMinPower] = useState<number | undefined>(initialQueryState.powerMin ?? undefined);
    const [maxPower, setMaxPower] = useState<number | undefined>(initialQueryState.powerMax ?? undefined);
    const [diameterMin, setDiameterMin] = useState<number | undefined>(initialQueryState.diameterMin ?? undefined);
    const [diameterMax, setDiameterMax] = useState<number | undefined>(initialQueryState.diameterMax ?? undefined);
    const [recent, setRecent] = useState(initialQueryState.recent ?? '0');

    const actualPage = useActualPageStateValue();
    const setActualPage = useSetActualPageState();
    const [disablePreloader, setDisablePreloader] = useState(false);

    const setFeedbackPopupDataName = useSetFeedbackPopupDataNameState();

    const queryParams = useMemo(() => {
        const params: any = {
            limit: CASUAL_LIMIT,
            page,
            category,
            search: search && search !== '' ? search : undefined,
            available,
            sort,
            order,
            'materials[]': materials,
            'brands[]': brands,
            'state[]': productState,
            'type[]': type,
            'color[]': color,
            'insert[]': insert,
            'genders[]': genders,
            'collection[]': collection,
            'dial_color[]': dialColors,
            'bracelet[]': bracelets,
            'mechanism[]': mechanisms,
            'options[]': options,
            'shapes[]': shapes,
            priceMin: isNaN(Number(priceMin)) ? undefined : Number(priceMin),
            priceMax: isNaN(Number(priceMax)) ? undefined : Number(priceMax),
            powerMin: isNaN(Number(minPower)) ? undefined : Number(minPower),
            powerMax: isNaN(Number(maxPower)) ? undefined : Number(maxPower),
            diameterMin: isNaN(Number(diameterMin)) ? undefined : Number(diameterMin),
            diameterMax: isNaN(Number(diameterMax)) ? undefined : Number(diameterMax),
        };

        if (recent && recent !== '0') {
            params.recent = recent;
        }

        return params;
    }, [
        page,
        category,
        search,
        available,
        sort,
        order,
        materials,
        brands,
        productState,
        type,
        color,
        insert,
        genders,
        collection,
        dialColors,
        bracelets,
        mechanisms,
        options,
        shapes,
        priceMin,
        priceMax,
        minPower,
        maxPower,
        diameterMin,
        diameterMax,
        recent,
    ]);

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, remove } = useProductsInfiniteQuery({
        params: queryParams,
        initialData: initialResponse,
    });

    const allProducts = data?.pages.flatMap((page) => page.data?.items || []) || [];

    const sortedItems = data
        ? allProducts.sort((a, b) => {
              if (a.price === 0 && b.price !== 0) return 1;
              if (a.price !== 0 && b.price === 0) return -1;
              return 0;
          })
        : [];

    const totalCount = data?.pages[0].data?.totalCount;
    const pagesCount = totalCount ? Math.ceil(totalCount / CASUAL_LIMIT) : 1;

    const handleLoadMore = () => {
        if (!isFetchingNextPage && hasNextPage) {
            setActualPage((prev) => prev + 1);
            fetchNextPage();
        }
    };

    const handlePageChange = (number: number) => {
        setDisablePreloader(false);
        unlockBodyScroll();
        document.body.classList.remove('is-page-leaving');
        if (wrapper.current) {
            wrapper.current.scrollIntoView({ behavior: 'smooth' });
        }

        if (number === page) {
            setDisablePreloader(true);
            remove();
            setActualPage(page);
            return;
        }

        setTimeout(() => {
            router.push({ pathname: router.pathname, query: { ...router.query, page: number } }, undefined, {
                shallow: true,
            });
        }, 500);
    };

    useEffect(() => {
        setSearchCount(totalCount || 0);
        if (typeof router.query.search === 'string') {
            setSearchTitle(router.query.search);
        }
    }, [totalCount, router.query.search, setSearchCount, setSearchTitle]);

    useEffect(() => {
        if (!router.query.category) {
            return;
        }

        if (router.query.priceMin) {
            if (Number(router.query.priceMin) !== priceMin && typeof router.query.priceMin === 'string') {
                setPriceMin(Number(router.query.priceMin));
            }
        } else {
            setPriceMin(undefined);
        }

        if (router.query.priceMax) {
            if (Number(router.query.priceMax) !== priceMax && typeof router.query.priceMax === 'string') {
                setPriceMax(Number(router.query.priceMax));
            }
        } else {
            setPriceMax(undefined);
        }

        if (router.query.powerMin) {
            if (Number(router.query.powerMin) !== minPower && typeof router.query.powerMin === 'string') {
                setMinPower(Number(router.query.powerMin));
            }
        } else {
            setMinPower(undefined);
        }

        if (router.query.powerMax) {
            if (Number(router.query.powerMax) !== maxPower && typeof router.query.powerMax === 'string') {
                setMaxPower(Number(router.query.powerMax));
            }
        } else {
            setMaxPower(undefined);
        }

        if (search !== router.query.search) {
            setSearch(router.query.search);
        }
        if (category && category.toString() !== router.query.category && router.query.category) {
            setCategory(Number(router.query.category));
            setPriceMin(undefined);
            setPriceMax(undefined);
            setMinPower(undefined);
            setMaxPower(undefined);
        }

        if (
            ((available !== null && typeof available !== 'undefined') ||
                typeof router.query.available !== 'undefined') &&
            available !== Number(router.query.available)
        ) {
            setAvailable(router.query.available ? Number(router.query.available) : null);
        }

        if (sort !== router.query.sort) {
            setSort(router.query.sort);
        }
        if (order !== router.query.order) {
            setOrder(router.query.order);
        }

        if (router.query['materials[]']) {
            if (
                Array.isArray(router.query['materials[]']) &&
                !equalArrays(
                    materials,
                    router.query['materials[]'].map((item) => Number(item)),
                )
            ) {
                setMaterials(router.query['materials[]'].map((item) => Number(item)));
            }
            if (
                !Array.isArray(router.query['materials[]']) &&
                !equalArrays(materials, [Number(router.query['materials[]'])])
            ) {
                setMaterials([Number(router.query['materials[]'])]);
            }
        } else {
            if (materials && materials.length > 0) {
                setMaterials([]);
            }
        }

        if (router.query['brands[]']) {
            if (
                Array.isArray(router.query['brands[]']) &&
                !equalArrays(
                    brands,
                    router.query['brands[]'].map((item) => Number(item)),
                )
            ) {
                setBrands(router.query['brands[]'].map((item) => Number(item)));
            }

            if (!Array.isArray(router.query['brands[]']) && !equalArrays(brands, [Number(router.query['brands[]'])])) {
                setBrands([Number(router.query['brands[]'])]);
            }
        } else {
            if (brands && brands.length > 0) {
                setBrands([]);
            }
        }

        if (router.query['state[]']) {
            if (Array.isArray(router.query['state[]']) && !equalArrays(productState, router.query['state[]'])) {
                setProductState(router.query['state[]']);
            }

            if (!Array.isArray(router.query['state[]']) && !equalArrays(productState, [router.query['state[]']])) {
                setProductState([router.query['state[]']]);
            }
        } else {
            if (productState && productState.length > 0) {
                setProductState([]);
            }
        }

        if (router.query['type[]']) {
            if (Array.isArray(router.query['type[]']) && !equalArrays(type, router.query['type[]'])) {
                setType(router.query['type[]']);
            }

            if (!Array.isArray(router.query['type[]']) && !equalArrays(type, [router.query['type[]']])) {
                setType([router.query['type[]']]);
            }
        } else {
            if (type && type.length > 0) {
                setType([]);
            }
        }

        if (router.query.diameterMin) {
            if (Number(router.query.diameterMin) !== diameterMin && typeof router.query.diameterMin === 'string') {
                setDiameterMin(Number(router.query.diameterMin));
            }
        } else {
            setDiameterMin(undefined);
        }

        if (router.query.diameterMax) {
            if (Number(router.query.diameterMax) !== diameterMax && typeof router.query.diameterMax === 'string') {
                setDiameterMax(Number(router.query.diameterMax));
            }
        } else {
            setDiameterMax(undefined);
        }

        if (router.query['color[]']) {
            if (Array.isArray(router.query['color[]']) && !equalArrays(color, router.query['color[]'])) {
                setColor(router.query['color[]']);
            }

            if (!Array.isArray(router.query['color[]']) && !equalArrays(color, [router.query['color[]']])) {
                setColor([router.query['color[]']]);
            }
        } else {
            if (color && color.length > 0) {
                setColor([]);
            }
        }

        if (router.query['insert[]']) {
            if (Array.isArray(router.query['insert[]']) && !equalArrays(insert, router.query['insert[]'])) {
                setInsert(router.query['insert[]']);
            }

            if (!Array.isArray(router.query['insert[]']) && !equalArrays(insert, [router.query['insert[]']])) {
                setInsert([router.query['insert[]']]);
            }
        } else {
            if (insert && insert.length > 0) {
                setInsert([]);
            }
        }

        if (router.query['collection[]']) {
            if (Array.isArray(router.query['collection[]']) && !equalArrays(collection, router.query['collection[]'])) {
                setCollection(router.query['collection[]']);
            }

            if (
                !Array.isArray(router.query['collection[]']) &&
                !equalArrays(collection, [router.query['collection[]']])
            ) {
                setCollection([router.query['collection[]']]);
            }
        } else {
            if (collection && collection.length > 0) {
                setCollection([]);
            }
        }

        if (router.query['dial_color[]']) {
            if (Array.isArray(router.query['dial_color[]']) && !equalArrays(dialColors, router.query['dial_color[]'])) {
                setDialColors(router.query['dial_color[]']);
            }

            if (
                !Array.isArray(router.query['dial_color[]']) &&
                !equalArrays(dialColors, [router.query['dial_color[]']])
            ) {
                setDialColors([router.query['dial_color[]']]);
            }
        } else {
            if (dialColors && dialColors.length > 0) {
                setDialColors([]);
            }
        }

        if (router.query['bracelets[]']) {
            if (Array.isArray(router.query['bracelets[]']) && !equalArrays(bracelets, router.query['bracelets[]'])) {
                setBracelets(router.query['bracelets[]']);
            }

            if (!Array.isArray(router.query['bracelets[]']) && !equalArrays(bracelets, [router.query['bracelets[]']])) {
                setBracelets([router.query['bracelets[]']]);
            }
        } else {
            if (bracelets && bracelets.length > 0) {
                setBracelets([]);
            }
        }

        if (router.query['mechanisms[]']) {
            if (Array.isArray(router.query['mechanisms[]']) && !equalArrays(mechanisms, router.query['mechanisms[]'])) {
                setMechanisms(router.query['mechanisms[]']);
            }

            if (
                !Array.isArray(router.query['mechanisms[]']) &&
                !equalArrays(mechanisms, [router.query['mechanisms[]']])
            ) {
                setMechanisms([router.query['mechanisms[]']]);
            }
        } else {
            if (mechanisms && mechanisms.length > 0) {
                setMechanisms([]);
            }
        }

        if (router.query['options[]']) {
            if (Array.isArray(router.query['options[]']) && !equalArrays(options, router.query['options[]'])) {
                setOptions(router.query['options[]']);
            }

            if (!Array.isArray(router.query['options[]']) && !equalArrays(options, [router.query['options[]']])) {
                setOptions([router.query['options[]']]);
            }
        } else {
            if (options && options.length > 0) {
                setOptions([]);
            }
        }

        if (router.query['shapes[]']) {
            if (Array.isArray(router.query['shapes[]']) && !equalArrays(shapes, router.query['shapes[]'])) {
                setShapes(router.query['shapes[]']);
            }

            if (!Array.isArray(router.query['shapes[]']) && !equalArrays(shapes, [router.query['shapes[]']])) {
                setShapes([router.query['shapes[]']]);
            }
        } else {
            if (shapes && shapes.length > 0) {
                setShapes([]);
            }
        }

        if (router.query['genders[]']) {
            if (
                Array.isArray(router.query['genders[]']) &&
                !equalArrays(
                    genders,
                    router.query['genders[]'].map((item) => Number(item)),
                )
            ) {
                setGenders(router.query['genders[]'].map((item) => Number(item)));
            }

            if (
                !Array.isArray(router.query['genders[]']) &&
                !equalArrays(genders, [Number(router.query['genders[]'])])
            ) {
                setGenders([Number(router.query['genders[]'])]);
            }
        } else {
            if (genders && genders.length > 0) {
                setGenders([]);
            }
        }
    }, [
        available,
        category,
        page,
        router.query,
        search,
        sort,
        order,
        materials,
        brands,
        productState,
        type,
        color,
        insert,
        priceMin,
        priceMax,
        minPower,
        maxPower,
        genders,
        collection,
        dialColors,
        diameterMin,
        diameterMax,
        bracelets,
        mechanisms,
        options,
        shapes,
    ]);

    useEffect(() => {
        setActualPage(page);
    }, [page, setActualPage]);

    return data && allProducts.length > 0 ? (
        <div className="cards-list-with-pagination catalog-list" ref={wrapper}>
            <ul className="list-unstyled cards-list">
                <AnimatePresence>
                    {isFetching && !isFetchingNextPage && !disablePreloader && <CatalogPreloader />}
                </AnimatePresence>
                {sortedItems.map((item, i) => {
                    return (
                        <Fragment key={item.id}>
                            {title && i === titleIndex && (
                                <li className="cards-list__item card-list-title">
                                    <div className="mobile-filters">
                                        <Button
                                            variant="primary"
                                            className="catalog-filter-btn"
                                            onClick={() => {
                                                openPopup(FILTERS_POPUP_NAME);
                                            }}
                                            size="sm"
                                        >
                                            Фильтр
                                        </Button>
                                        <Button
                                            variant="primary"
                                            className="catalog-filter-btn"
                                            onClick={() => {
                                                openPopup(SORT_POPUP_NAME);
                                            }}
                                            size="sm"
                                        >
                                            Сортировка
                                        </Button>
                                    </div>
                                </li>
                            )}

                            <li className="cards-list__item">
                                <Card
                                    productId={item.id}
                                    slug={item.slug}
                                    cardName={item.name}
                                    price={item.price}
                                    materialName={item.material}
                                    size={item.size}
                                    state={item.state}
                                    available={item.available}
                                    images={item.images}
                                    reference={item.reference}
                                    diameter={item.diameter}
                                    isStandardForm={item.is_standard_form}
                                    accessories={item.accessories}
                                    productColor={item.color}
                                    type={item.type}
                                    params={item.params}
                                    insert={item.insert}
                                    categoryId={category}
                                />
                            </li>
                        </Fragment>
                    );
                })}
            </ul>
            <div
                className={classNames('cards-list-pagination-btns-wrapper', {
                    'pagination-wrapper-double': hasNextPage,
                })}
            >
                {hasNextPage && (
                    <Button
                        className="load-more-btn"
                        geometryVariant="mustache"
                        icon={
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                                src={ArrowMoreSVG}
                                alt=""
                                width={13}
                                height={12}
                                className="img-fluid"
                                decoding="async"
                            />
                        }
                        onClick={handleLoadMore}
                        disabled={isFetchingNextPage}
                    >
                        {isFetchingNextPage ? 'Загрузка...' : 'Показать ещё'}
                    </Button>
                )}
                {pagesCount > 1 && (
                    <NewPagination totalPages={pagesCount} activePage={actualPage} onClickHandler={handlePageChange} />
                )}
            </div>
        </div>
    ) : (
        <div className="catalog-empty">
            <div className="text-l" dangerouslySetInnerHTML={{ __html: catalogEmptyText }} />
            <Button
                geometryVariant="mustache"
                onClick={() => {
                    setServiceValueFormState(catalogEmptyText);
                    setFeedbackPopupDataName('empty');
                    openPopup(FEEDBACK_POPUP_NAME);
                }}
            >
                Найти товар
            </Button>
        </div>
    );
};

export default CatalogListWithPagination;
